import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { PlayerProvider } from "./context/PlayerContext";
import { CartProvider } from "./context/CartContext";
import { Navbar } from "./components/Navbar";
import { AnnouncementBanner } from "./components/AnnouncementBanner";
import { Player } from "./components/Player";
import { WaitlistPopup } from "./components/WaitlistPopup";
import AuthCallback from "./components/AuthCallback";

// Pages
import HomePage from "./pages/HomePage";
import MusicPage from "./pages/MusicPage";
import MerchPage from "./pages/MerchPage";
import TokensPage from "./pages/TokensPage";
import TrackDetailPage from "./pages/TrackDetailPage";
import AlbumDetailPage from "./pages/AlbumDetailPage";
import MerchDetailPage from "./pages/MerchDetailPage";
import AuthPage from "./pages/AuthPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import CartPage from "./pages/CartPage";
import CheckoutSuccessPage from "./pages/CheckoutSuccessPage";
import PreorderSuccessPage from "./pages/PreorderSuccessPage";
import DashboardPage from "./pages/DashboardPage";
import AdminPage from "./pages/AdminPage";
import PlaylistsPage from "./pages/PlaylistsPage";
import VideosPage from "./pages/VideosPage";
import SubscriptionPage from "./pages/SubscriptionPage";
import MarketplacePage from "./pages/MarketplacePage";
import AnalyticsPage from "./pages/AnalyticsPage";
import LeaderboardPage from "./pages/LeaderboardPage";
import LiveEventsPage from "./pages/LiveEventsPage";
import PresavesPage from "./pages/PresavesPage";
import FanProfilePage from "./pages/FanProfilePage";
import BroadcastStudioPage from "./pages/BroadcastStudioPage";
import BonusPreviewPage from "./pages/BonusPreviewPage";

// Protected Route wrapper with OAuth callback handling
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  const location = useLocation();
  
  // Check if this is an OAuth callback (has session_id in hash)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Fires a GA4 page_view on every SPA route change. Index.html installs gtag once
// with send_page_view:false, so this is the single source of truth for page_views.
const AnalyticsTracker = () => {
  const location = useLocation();
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    const page_path = location.pathname + location.search;
    window.gtag("event", "page_view", {
      page_path,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location.pathname, location.search]);
  return null;
};

function AppRoutes() {
  return (
    <>
      <AnalyticsTracker />
      <Navbar />
      <AnnouncementBanner />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/music" element={<MusicPage />} />
        <Route path="/merch" element={<MerchPage />} />
        <Route path="/tokens" element={<TokensPage />} />
        <Route path="/track/:id" element={<TrackDetailPage />} />
        <Route path="/album/:id" element={<AlbumDetailPage />} />
        <Route path="/merch/:id" element={<MerchDetailPage />} />
        <Route path="/login" element={<AuthPage />} />
        <Route path="/register" element={<AuthPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/cart" element={<ProtectedRoute><CartPage /></ProtectedRoute>} />
        <Route path="/checkout/success" element={<ProtectedRoute><CheckoutSuccessPage /></ProtectedRoute>} />
        <Route path="/presave/preorder/success" element={<ProtectedRoute><PreorderSuccessPage /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/playlists" element={<PlaylistsPage />} />
        <Route path="/videos" element={<VideosPage />} />
        <Route path="/subscription" element={<SubscriptionPage />} />
        <Route path="/marketplace" element={<MarketplacePage />} />
        <Route path="/leaderboard" element={<LeaderboardPage />} />
        <Route path="/live" element={<LiveEventsPage />} />
        <Route path="/studio" element={<ProtectedRoute><BroadcastStudioPage /></ProtectedRoute>} />
        <Route path="/presaves" element={<PresavesPage />} />
        <Route path="/pre-save" element={<PresavesPage />} />
        <Route path="/fan/:code" element={<FanProfilePage />} />
        <Route path="/bonus/:presaveId" element={<BonusPreviewPage />} />
        <Route path="/analytics" element={<ProtectedRoute adminOnly><AnalyticsPage /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Player />
      <WaitlistPopup />
      <Toaster position="top-right" theme="dark" richColors />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PlayerProvider>
          <CartProvider>
            <AppRoutes />
          </CartProvider>
        </PlayerProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
